import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Package, Plus, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader, Segmented, StatusPill } from '@/components/page';
import {
  AccessTag,
  AppMark,
  EmptyState,
  ListFooter,
  ago,
  fmtDateTime,
} from '@/components/products/shared';
import { useProducts, useAuth, useDebouncedValue } from '@/hooks';
import type { Product, ProductAccessModel } from '@/types';

const PAGE_SIZE = 10;
const SYSTEM_ORG_ID = 'org-system';
type ModelFilter = 'all' | ProductAccessModel;

export function ProductsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.is_super_admin === true;
  const { data: products = [], isLoading, isError, refetch } = useProducts();

  const [search, setSearch] = useState('');
  const [model, setModel] = useState<ModelFilter>('all');
  const [org, setOrg] = useState('all');
  const [page, setPage] = useState(1);
  const debounced = useDebouncedValue(search);

  const modelOf = (p: Product): ProductAccessModel => p.settings?.access_model ?? 'gated';
  const count = (m: ModelFilter) =>
    products.filter((p) => m === 'all' || modelOf(p) === m).length;

  const orgs = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of products) {
      if (p.organization_id && p.organization_id !== SYSTEM_ORG_ID) {
        map.set(p.organization_id, p.organization_name || p.organization_id);
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [products]);

  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    return products.filter(
      (p) =>
        (model === 'all' || modelOf(p) === model) &&
        (org === 'all' || p.organization_id === org) &&
        (!q || p.name.toLowerCase().includes(q) || p.slug.includes(q))
    );
  }, [products, debounced, model, org]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const rows = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const hrefOf = (p: Product) => (canManage ? `/products/${p.id}` : `/access#${p.id}`);
  const clearFilters = () => {
    setSearch('');
    setModel('all');
    setOrg('all');
    setPage(1);
  };

  const newApp = canManage && (
    <Button asChild>
      <Link to="/products/new">
        <Plus className="h-4 w-4" />
        New app
      </Link>
    </Button>
  );

  return (
    <div>
      <PageHeader
        title="Apps"
        description={
          canManage
            ? 'Apps are the resources people sign in to. Each one owns its roles, access grants and OAuth clients.'
            : 'Apps you have been granted access to, with your roles in each.'
        }
        actions={newApp || undefined}
      />

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="divide-y divide-border" aria-busy="true" aria-label="Loading apps">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex animate-pulse items-center gap-3 px-[18px] py-3.5">
                <div className="h-[34px] w-[34px] rounded-lg bg-secondary" />
                <div className="space-y-2">
                  <div className="h-3.5 w-40 rounded bg-secondary" />
                  <div className="h-3 w-24 rounded bg-secondary" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Couldn’t load apps"
            body="Check your connection and try again."
            action={
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : products.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No apps yet"
            body={
              canManage
                ? 'Create an app to define roles, grant access and register OAuth clients.'
                : 'When an administrator grants you access to an app, it appears here.'
            }
            action={newApp || undefined}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 border-b border-border px-[18px] py-3">
              <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  aria-label="Search apps"
                  placeholder="Search name or slug"
                  autoComplete="off"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Segmented<ModelFilter>
                  value={model}
                  onChange={(v) => {
                    setModel(v);
                    setPage(1);
                  }}
                  options={[
                    { value: 'all', label: 'All', count: count('all') },
                    { value: 'gated', label: 'Gated', count: count('gated') },
                    { value: 'org_members', label: 'Org members', count: count('org_members') },
                    { value: 'open', label: 'Open', count: count('open') },
                  ]}
                />
                {orgs.length > 1 && (
                  <Select
                    value={org}
                    onValueChange={(v) => {
                      setOrg(v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger aria-label="Organization" className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All organizations</SelectItem>
                      {orgs.map(([id, name]) => (
                        <SelectItem key={id} value={id}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>App</TableHead>
                  <TableHead className="hidden md:table-cell">Organization</TableHead>
                  <TableHead>Access</TableHead>
                  {canManage && <TableHead className="text-right">Clients</TableHead>}
                  {canManage && (
                    <TableHead className="hidden text-right md:table-cell">Grants</TableHead>
                  )}
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Updated</TableHead>
                  <TableHead className="w-8">
                    <span className="sr-only">Open</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={8}>
                      <EmptyState
                        icon={Search}
                        title="No apps match"
                        body="Try another name, access model or organization."
                        action={
                          <Button variant="outline" onClick={clearFilters}>
                            Clear filters
                          </Button>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((p) => (
                    <TableRow
                      key={p.id}
                      onClick={() => navigate(hrefOf(p))}
                      className={`cursor-pointer ${p.is_active ? '' : 'opacity-60'}`}
                    >
                      <TableCell>
                        <Link
                          to={hrefOf(p)}
                          className="flex min-w-0 items-center gap-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <AppMark name={p.name} />
                          <span className="min-w-0">
                            <b className="block max-w-[260px] truncate font-semibold" title={p.name}>
                              {p.name}
                            </b>
                            <span
                              className="block max-w-[260px] truncate font-mono text-xs text-muted-foreground"
                              title={p.slug}
                            >
                              {p.slug}
                            </span>
                          </span>
                        </Link>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {p.organization_id && p.organization_id !== SYSTEM_ORG_ID ? (
                          <Link
                            to={`/organizations/${p.organization_id}`}
                            className="text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {p.organization_name || p.organization_id}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <AccessTag model={modelOf(p)} />
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right font-mono tabular-nums">
                          {p.client_count ?? '—'}
                        </TableCell>
                      )}
                      {canManage && (
                        <TableCell className="hidden text-right font-mono tabular-nums md:table-cell">
                          {p.grant_count ?? '—'}
                        </TableCell>
                      )}
                      <TableCell>
                        <StatusPill tone={p.is_active ? 'ok' : 'neutral'}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </StatusPill>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span
                          className="whitespace-nowrap font-mono text-xs tabular-nums"
                          title={fmtDateTime(p.updated_at)}
                        >
                          {ago(p.updated_at)}
                        </span>
                      </TableCell>
                      <TableCell className="w-8 text-muted-foreground">
                        <ChevronRight className="h-4 w-4" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <ListFooter
              page={current}
              pageCount={pageCount}
              total={filtered.length}
              pageSize={PAGE_SIZE}
              noun="apps"
              onPage={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}
